import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useEnrollments, useUserEnrollments } from '@/hooks/useEnrollments'
import { useMyMembership } from '@/hooks/useMembership'
import { useMyPaymentProofs } from '@/hooks/usePaymentProofs'
import { TransferTicketModal } from '@/components/enrollment/TransferTicketModal'
import { invitationService, PendingInvitation } from '@/services/invitationService'
import { transferService } from '@/services/transferService'
import { useNotificationsByUser, useMarkAsRead } from '@/hooks/useNotifications'
import { getNotificationIcon, getNotificationColor, formatTimeAgo } from '@/types/notification'
import type { Notification } from '@/types/notification'
import { useMyCertificates } from '@/hooks/useCertificate'

export const DashboardPage: React.FC = () => {
    const { user, isVerified } = useAuth()
    const navigate = useNavigate()
    const { data: enrollments, isLoading, refetch: refetchEnrollments } = useEnrollments({ userId: user?.id })

    // Fetch pending_payment enrollments separately
    const { data: pendingEnrollmentsRes } = useUserEnrollments(user?.id || '', { status: 'pending_payment' })
    const pendingEnrollments = pendingEnrollmentsRes?.data || []

    // Fetch payment proofs to check submission status
    const { data: paymentProofs } = useMyPaymentProofs()
    const proofByEnrollment = new Map((paymentProofs || []).map((p: any) => [p.enrollmentId, p]))

    // Filter out pending_payment enrollments — they shouldn't appear in My Learning
    // until payment is confirmed.
    const activeEnrollments = enrollments?.filter((e: any) => e.paymentStatus !== 'pending') || []

    const [transferEnrollmentId, setTransferEnrollmentId] = useState<string | null>(null)
    const [eligibleEnrollments, setEligibleEnrollments] = useState<Set<string>>(new Set())
    const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([])
    const [claimingId, setClaimingId] = useState<string | null>(null)

    // Check transfer eligibility for each active enrollment
    useEffect(() => {
        if (!enrollments) return
        const check = async () => {
            const results = await Promise.allSettled(
                enrollments.map(async (e: any) => {
                    const res = await transferService.checkEligibility(e.id)
                    return { id: e.id, eligible: res.eligible }
                })
            )
            const eligible = new Set<string>()
            results.forEach((r) => {
                if (r.status === 'fulfilled' && r.value.eligible) eligible.add(r.value.id)
            })
            setEligibleEnrollments(eligible)
        }
        check()
    }, [enrollments])

    // Fetch pending invitations
    useEffect(() => {
        invitationService.listPending()
            .then(setPendingInvitations)
            .catch(() => {/* silently skip if endpoint fails */})
    }, [])

    const handleClaim = async (inv: PendingInvitation) => {
        setClaimingId(inv.id)
        try {
            await invitationService.claim(inv.id)
            setPendingInvitations((prev) => prev.filter((i) => i.id !== inv.id))
            refetchEnrollments()
        } catch (err: any) {
            alert(err?.message || 'Gagal klaim undangan')
        } finally {
            setClaimingId(null)
        }
    }

    const completedCount = activeEnrollments?.filter(e => e.progress === 100).length || 0
    const verified = isVerified()

    const { data: notificationsData } = useNotificationsByUser(user?.id || '', { limit: 3 })
    const notifications = ((notificationsData as any)?.notifications || []) as Notification[]
    const markAsRead = useMarkAsRead()

    const { data: certificates } = useMyCertificates(user?.id || '')
    const { data: membership } = useMyMembership()

    const handleNotificationClick = async (n: Notification) => {
        if (!n.isRead) {
            try { await markAsRead.mutateAsync(n.id) } catch { /* ignore */ }
        }
        if (n.actionLink) navigate(n.actionLink)
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-24 md:pt-32 pb-32">
            {transferEnrollmentId && (
                <TransferTicketModal
                    isOpen={true}
                    onClose={() => setTransferEnrollmentId(null)}
                    enrollmentId={transferEnrollmentId}
                    onSuccess={() => { setTransferEnrollmentId(null); refetchEnrollments() }}
                />
            )}
            {!verified && (
                <div className="fixed top-20 left-0 right-0 z-40 bg-rose-500 text-white px-6 py-3 shadow-lg">
                    <div className="max-w-[1440px] mx-auto flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined">warning</span>
                            <p className="text-sm font-bold">Verifikasi email Anda untuk membuka semua fitur transaksi.</p>
                        </div>
                        <button onClick={() => navigate('/verify-email')} className="bg-white text-rose-500 px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-opacity-90 transition-all">
                            Verifikasi Sekarang
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                {/* Header Section */}
                <div className="relative mb-8 p-6 bg-slate-900 dark:bg-white rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
                    <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/20 to-transparent opacity-50" />
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-2">
                            <h1 className="text-2xl md:text-3xl font-bold text-white dark:text-slate-900 tracking-tight leading-none">
                                Halo, <span className="text-primary">{user?.name || 'Siswa'}</span>! 👋
                            </h1>
                            <p className="text-slate-400 dark:text-slate-500 text-sm font-medium">
                                Senang melihatmu kembali. Siap untuk melanjutkan materi hari ini?
                            </p>
                        </div>
                        <Link to="/discover" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-2xl font-bold text-sm hover:opacity-90 active:scale-95 transition-all w-fit shadow-xl shadow-primary/20">
                            Jelajahi Kursus Baru
                            <span className="material-symbols-outlined text-[20px]">explore</span>
                        </Link>
                    </div>
                </div>

                {/* Statistics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-10">
                    <StatCard
                        icon="school"
                        label="Kursus Aktif"
                        value={activeEnrollments?.length || 0}
                        color="text-primary bg-primary/10"
                    />
                    <StatCard
                        icon="verified"
                        label="Selesai"
                        value={completedCount}
                        color="text-green-500 bg-green-500/10"
                    />
                    <StatCard
                        icon="schedule"
                        label="Waktu Belajar"
                        value="12j" // TODO: Add backend endpoint for aggregated study time
                        color="text-blue-500 bg-blue-500/10"
                    />
                    <StatCard
                        icon="workspace_premium"
                        label="Sertifikat"
                        value={(certificates as any)?.length || 0}
                        color="text-amber-500 bg-amber-500/10"
                    />
                </div>

                {/* Membership Quick Access */}
                {membership && (
                    <Link
                        to="/dashboard/membership"
                        className="block mb-10 p-5 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent dark:from-amber-500/10 dark:via-orange-500/5 dark:to-transparent rounded-2xl border border-amber-200/50 dark:border-amber-500/20 hover:shadow-lg hover:border-amber-300/50 transition-all group"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="size-10 bg-amber-500/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <span className="material-symbols-outlined text-amber-500 text-[20px]">emoji_events</span>
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                                        Membership Tier <span className="text-amber-600 dark:text-amber-400 capitalize">{membership.tier === 'none' ? 'Peserta' : membership.tier}</span>
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                        {membership.totalAttendance} kehadiran total &middot; Lihat progres dan manfaat
                                    </p>
                                </div>
                            </div>
                            <span className="material-symbols-outlined text-amber-400/50 text-[20px] group-hover:text-amber-500 group-hover:translate-x-1 transition-all">arrow_forward</span>
                        </div>
                    </Link>
                )}

                {/* Notifikasi Section */}
                {notifications.length > 0 && (
                    <div className="mb-10">
                        <div className="flex items-center justify-between mb-6 border-b border-slate-200 dark:border-white/10 pb-6">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Notifikasi Terbaru</h2>
                            <span className="text-sm font-bold text-slate-400">{notifications.length} Terbaru</span>
                        </div>
                        <div className="space-y-3">
                            {notifications.map((n: Notification) => {
                                const icon = getNotificationIcon(n.type)
                                const colorClass = getNotificationColor(n.type)
                                return (
                                    <button key={n.id} onClick={() => handleNotificationClick(n)} className={`w-full flex items-start gap-4 p-4 rounded-2xl border transition-all text-left cursor-pointer ${
                                        !n.isRead ? 'bg-primary/5 border-primary/20 hover:bg-primary/10' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5'
                                    }`}>
                                        <div className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
                                            <span className={`material-symbols-outlined text-[20px] ${!n.isRead ? 'filled' : ''}`}>{icon}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm line-clamp-1 ${!n.isRead ? 'font-black text-slate-900 dark:text-white' : 'font-bold text-slate-700 dark:text-slate-300'}`}>
                                                {n.title}
                                            </p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{formatTimeAgo(n.createdAt)}</p>
                                        </div>
                                        {!n.isRead && <div className="size-2.5 bg-primary rounded-full shrink-0 mt-2 shadow-sm shadow-primary/50" />}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Pending Invitations Section */}
                {pendingInvitations.length > 0 && (
                    <div className="mb-10">
                        <div className="flex items-center justify-between mb-6 border-b border-slate-200 dark:border-white/10 pb-6">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Undangan Kursus</h2>
                            <span className="text-sm font-bold text-amber-500">{pendingInvitations.length} Menunggu Klaim</span>
                        </div>
                        <div className="space-y-4">
                            {pendingInvitations.map((inv) => (
                                <div key={inv.id} className="flex items-center justify-between p-5 bg-amber-50 dark:bg-amber-500/10 rounded-2xl border border-amber-100 dark:border-amber-500/20">
                                    <div>
                                        <p className="font-bold text-slate-900 dark:text-white text-sm">Undangan ke kursus</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{inv.invitedEmail}</p>
                                    </div>
                                    <button
                                        onClick={() => handleClaim(inv)}
                                        disabled={claimingId === inv.id}
                                        className="bg-amber-500 text-white px-5 py-2 rounded-xl text-xs font-bold hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
                                    >
                                        {claimingId === inv.id ? 'Memproses...' : 'Klaim'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Menunggu Pembayaran Section */}
                {pendingEnrollments.length > 0 && (
                    <div className="mb-10">
                        <div className="flex items-center justify-between mb-6 border-b border-slate-200 dark:border-white/10 pb-6">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Menunggu Pembayaran</h2>
                            <span className="text-sm font-bold text-amber-500">{pendingEnrollments.length} Kursus</span>
                        </div>
                        <div className="space-y-4">
                            {pendingEnrollments.map((enrollment: any) => {
                                const proof = proofByEnrollment.get(enrollment.id)
                                const hasProof = !!proof
                                const badgeLabel = hasProof
                                    ? (proof.status === 'pending' ? 'Bukti Terkirim'
                                        : proof.status === 'approved' ? 'Disetujui'
                                        : proof.status === 'rejected' ? 'Ditolak'
                                        : 'Menunggu Verifikasi')
                                    : 'Belum Upload Bukti'
                                const badgeClass = hasProof
                                    ? (proof.status === 'pending' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                                        : proof.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                                        : proof.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                                        : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400')
                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                                const badgeIcon = hasProof
                                    ? (proof.status === 'pending' ? 'send'
                                        : proof.status === 'approved' ? 'check_circle'
                                        : proof.status === 'rejected' ? 'cancel'
                                        : 'schedule')
                                    : 'upload'
                                return (
                                    <div
                                        key={enrollment.id}
                                        className="flex items-center gap-4 p-5 bg-amber-50 dark:bg-amber-500/10 rounded-2xl border border-amber-100 dark:border-amber-500/20"
                                    >
                                        {/* Course thumbnail */}
                                        <div className="w-16 h-16 rounded-xl bg-slate-100 dark:bg-white/5 flex-shrink-0 overflow-hidden">
                                            {enrollment.course?.thumbnailUrl ? (
                                                <div
                                                    className="w-full h-full bg-cover bg-center"
                                                    style={{ backgroundImage: `url(${enrollment.course.thumbnailUrl})` }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-2xl text-slate-300">school</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                                                {enrollment.course?.title || 'Course'}
                                            </h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                Rp {(enrollment.originalPrice || 0).toLocaleString('id-ID')}
                                            </p>
                                            <div className="flex items-center gap-1.5 mt-2">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${badgeClass}`}>
                                                    <span className="material-symbols-outlined text-[12px]">{badgeIcon}</span>
                                                    {badgeLabel}
                                                </span>
                                            </div>
                                        </div>

                                        {/* CTA */}
                                        <button
                                            onClick={() => navigate('/dashboard/payments')}
                                            className="flex-shrink-0 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2 rounded-xl text-xs font-bold hover:opacity-90 active:scale-95 transition-all"
                                        >
                                            Lihat Status
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* My Courses Section */}
                <div>
                    <div className="flex items-center justify-between mb-8 border-b border-slate-200 dark:border-white/10 pb-6">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Kursus Saya</h2>
                        <span className="text-sm font-bold text-slate-400">{activeEnrollments?.length || 0} Total Kursus</span>
                    </div>

                    {isLoading && (
                        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-64 bg-white dark:bg-slate-900 rounded-[2rem] animate-pulse border border-slate-100 dark:border-white/5" />
                            ))}
                        </div>
                    )}

                    {!isLoading && (!activeEnrollments || activeEnrollments.length === 0) && (
                        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-100 dark:border-white/5 shadow-sm">
                            <div className="size-20 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                                <span className="material-symbols-outlined text-4xl">school</span>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Mulai Belajar Hari Ini</h3>
                            <p className="text-slate-500 dark:text-slate-400 font-medium mb-8 max-w-sm mx-auto leading-relaxed">
                                Anda belum terdaftar di kursus apapun. Temukan materi yang menarik bagi Anda sekarang!
                            </p>
                            <Link to="/discover" className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-8 py-3.5 rounded-2xl font-bold text-sm hover:opacity-90 active:scale-95 transition-all shadow-lg shadow-slate-200">
                                Cari Kursus
                            </Link>
                        </div>
                    )}

                    {!isLoading && activeEnrollments && activeEnrollments.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8 xl:gap-10">
                            {activeEnrollments.map((enrollment: any) => (
                                <div
                                    key={enrollment.id}
                                    className="group bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden border border-slate-100 dark:border-white/5 shadow-[0_10px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-all duration-500 hover:-translate-y-2 flex flex-col h-full"
                                >
                                    <Link to={`/learn/${enrollment.id}`} className="flex flex-col h-full">
                                        <div className="relative aspect-video overflow-hidden m-3 rounded-[1.5rem] bg-slate-100 dark:bg-white/5">
                                            {enrollment.course?.thumbnailUrl ? (
                                                <div
                                                    className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                                    style={{ backgroundImage: `url(${enrollment.course.thumbnailUrl})` }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-4xl text-slate-200 dark:text-slate-800">school</span>
                                                </div>
                                            )}
                                            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-xl shadow-sm border border-white/20">
                                                <span className="text-xs font-bold text-slate-900">{enrollment.course?.category || 'Pelatihan'}</span>
                                            </div>
                                        </div>

                                        <div className="px-5 pt-2 pb-6 flex-1 flex flex-col">
                                            <h3 className="text-base font-bold text-slate-900 dark:text-white leading-snug group-hover:text-primary transition-colors line-clamp-2 min-h-[3rem]">
                                                {enrollment.course?.title || 'Untitled Course'}
                                            </h3>

                                            <div className="space-y-3 mt-auto">
                                                <div className="flex items-center justify-between text-xs font-bold">
                                                    <span className="text-slate-400">Progress Belajar</span>
                                                    <span className="text-primary">{enrollment.progress || 0}%</span>
                                                </div>
                                                <div className="h-2 bg-slate-50 dark:bg-white/5 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full transition-all duration-1000 ${enrollment.progress === 100 ? 'bg-green-500' : 'bg-primary'}`}
                                                        style={{ width: `${enrollment.progress || 0}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>

                                    {eligibleEnrollments.has(enrollment.id) && (
                                        <div className="px-5 pb-5 pt-3">
                                            <button
                                                onClick={() => setTransferEnrollmentId(enrollment.id)}
                                                className="w-full flex items-center justify-center gap-2 py-2 border border-slate-200 dark:border-white/10 rounded-xl text-xs font-bold text-slate-500 dark:text-slate-400 hover:border-primary hover:text-primary transition-colors"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                                                Transfer Tiket
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const StatCard: React.FC<{ icon: string; label: string; value: string | number; color: string }> = ({ icon, label, value, color }) => (
    <div className="bg-white dark:bg-slate-900 p-2 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-100 dark:border-white/5 shadow-[0_10px_30px_rgba(0,0,0,0.02)] group hover:border-primary/20 transition-all duration-300">
        <div className={`size-6 sm:size-10 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-3 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6 ${color}`}>
            <span className="material-symbols-outlined text-[14px] sm:text-[20px]">{icon}</span>
        </div>
        <p className="text-sm sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">{value}</p>
        <p className="text-[10px] sm:text-sm font-bold text-slate-400 leading-tight mt-0.5">{label}</p>
    </div>
)

export default DashboardPage
